"use client";

import { useMemo, useState } from "react";
import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, NOTICE } from "@/components/tool-kit/styles";
import {
  CompactTextArea,
  DraftPanel,
  FieldGroup,
  FormHeader,
} from "@/components/tools/academic-correspondence/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildRecommendationLetter,
  EMPTY_EVIDENCE,
  ENDORSEMENTS,
  type Endorsement,
  EVIDENCE_SLOTS,
  RANKS,
  type Rank,
  REC_SIGN_OFFS,
  RECOMMENDATION_BANK,
  type RecommendationInput,
  type RecSignOff,
  type ReviewNote,
  reviewLetter,
} from "@/lib/tool-kit/admissions/recommendation-letter";
import { countPlaceholders } from "@/lib/tool-kit/correspondence/compose";
import { formatLetterDate } from "@/lib/tool-kit/correspondence/journal-cover-letter";

/** English strings. A translated content file overrides these through `ui`. */
export const RECOMMENDATION_LETTER_GENERATOR_STRINGS = {
  formHeading: "About the letter",
  tryExample: "Try an example",
  clear: "Clear",
  ferpaSummary: "Before you write: the FERPA waiver (United States)",
  ferpaPoints: [
    "On US application systems such as the Common App, the student is asked whether to waive the right to see recommendation letters. The choice is the student’s: under the federal rule (34 CFR §99.12) a college may not require the waiver as a condition of admission.",
    "A waiver is a written choice to give up the right to inspect confidential letters after enrolling. If the student did not waive, write as if the student may one day read the letter.",
    "Check which choice your system shows you. This is general information, not legal advice; for a specific case ask the college’s admissions office or registrar.",
  ],
  studentSection: "The student and the application",
  nameLabel: "Student’s full name",
  namePlaceholder: "Maya Rahman",
  shortNameLabel: "Name for later mentions (optional)",
  shortNamePlaceholder: "Maya",
  shortNameHint: "Usually the first name. Leave blank to repeat the full name.",
  purposeLabel: "What are you recommending them for? (optional)",
  purposePlaceholder: "the MSc in Chemistry at Northfield University",
  recipientLabel: "Addressed to (optional)",
  recipientPlaceholder: "Graduate Admissions Committee",
  recipientHint:
    "Follows “Dear”. Leave blank for “To the admissions committee,”.",
  dateLabel: "Date (optional)",
  relationshipSection: "How you know them",
  capacityLabel: "Your relationship",
  capacityPlaceholder:
    "her instructor in two chemistry courses and the supervisor of her senior thesis",
  capacityHint:
    "Finishes the sentence “I have known [the student] for [how long], as …”.",
  durationLabel: "For how long",
  durationPlaceholder: "three years",
  contextLabel: "Anything else about the setting (optional)",
  contextPlaceholder:
    "My courses enrol about 60 students each, and I supervise four theses a year.",
  evidenceSection: "What you have seen",
  evidenceHint:
    "Write in your own words. One concrete episode — what the student did, when, and what happened — persuades more than three adjectives.",
  qualityLabel: "Strength {n}",
  qualityPlaceholder: "intellectual curiosity",
  observationLabel: "What you observed (strength {n})",
  observationPlaceholder: "In my organic chemistry course, she asked why…",
  comparisonSection: "Comparison (optional)",
  comparisonHint:
    "Only include a comparison you can stand behind: name the group, and the basis you are judging on.",
  rankLabel: "Where does the student stand?",
  rankOptions: {
    none: "No comparison",
    top1: "Top 1%",
    top5: "Top 5%",
    top10: "Top 10%",
    top25: "Top 25%",
    own: "I will write it myself",
  },
  cohortLabel: "The group you are comparing with",
  cohortPlaceholder:
    "the roughly 300 undergraduates I have taught over eight years",
  basisLabel: "Judged by (optional)",
  basisPlaceholder: "the quality of independent research work",
  ownLabel: "Your own comparison",
  ownPlaceholder: "Of the students I have supervised, she is…",
  growthLabel: "Growth or character (optional)",
  growthPlaceholder:
    "She struggled in her first term, sought help, and finished the year at the top of her section.",
  limitLabel: "A limitation worth stating (optional)",
  limitPlaceholder: "public speaking, where she is still building confidence",
  limitHint:
    "Honest limits make the rest believable. Required if your endorsement is qualified.",
  endorsementSection: "Your endorsement",
  endorsementLabel: "Strength of endorsement",
  endorsementOptions: {
    strongest: "Without reservation",
    strong: "Strongly",
    solid: "Pleased to recommend",
    qualified: "With a qualification",
  },
  endorsementHint:
    "Choose the level you actually feel. Readers compare many letters; an honest “pleased to recommend” with real examples outweighs an inflated claim.",
  signSection: "Your details",
  signNameLabel: "Your name",
  signNamePlaceholder: "Elena Park",
  signTitleLabel: "Your title",
  signTitlePlaceholder: "Associate Professor of Chemistry",
  signInstitutionLabel: "Institution",
  signInstitutionPlaceholder: "Northfield University",
  signEmailLabel: "Email (optional)",
  signEmailPlaceholder: "e.park@example.edu",
  signOffLabel: "Sign-off",
  signOffOptions: {
    sincerely: "Sincerely,",
    bestRegards: "Best regards,",
    yoursSincerely: "Yours sincerely,",
  },
  draftHeading: "Your letter",
  copy: "Copy",
  copyPlain: "Copy plain text",
  copied: "Copied!",
  download: "Download .txt",
  downloadFilename: "recommendation-letter.txt",
  placeholdersLeft:
    "one:{count} placeholder left to fill — shown in [brackets]|other:{count} placeholders left to fill — shown in [brackets]",
  noPlaceholders: "No placeholders left in the letter.",
  reviewHeading: "Checks before you send",
  reviewNone:
    "Nothing flagged. These checks only catch gaps; reread the letter as the committee will.",
  wordCount: "one:{count} word|other:{count} words",
  review: {
    "no-evidence":
      "No observations yet. A letter needs at least one concrete episode.",
    "evidence-thin":
      "Observation {slot} is {words}. Add what the student did, when, and what happened.",
    "comparison-no-group":
      "A comparison needs a group. Say who the student is being compared with, and how many.",
    "qualified-no-limit":
      "You chose a qualified endorsement but have not said what the qualification is.",
    "no-relationship":
      "Say how you know the student and for how long; readers weigh a letter by how well the writer knows the student.",
    "letter-short":
      "The letter is {words}. Most are about a page — add specifics rather than adjectives.",
    "letter-long":
      "The letter is {words}. Past a page, readers skim; cut what does not add evidence.",
  },
  example: {
    name: "Maya Rahman",
    shortName: "Maya",
    purpose: "the MSc in Chemistry at Northfield University",
    recipient: "",
    capacity:
      "her instructor in two chemistry courses and the supervisor of her senior thesis",
    duration: "three years",
    context:
      "My courses enrol about 60 students each, and I supervise four theses a year.",
    quality1: "intellectual curiosity",
    observation1:
      "In my organic chemistry course, Maya asked why a reaction that worked on paper gave a poor yield in the lab, then spent two weeks of office hours testing her explanation with the teaching assistant. Her hypothesis was correct, and it changed how I teach that experiment.",
    quality2: "reliability under pressure",
    observation2:
      "When the department’s spectrometer failed the week before her thesis deadline, she rearranged her schedule, trained two classmates on the backup instrument, and still submitted on time.",
    quality3: "generosity with her peers",
    observation3:
      "She ran an unpaid weekly study session for first-year students that grew from four attendees to about twenty over the term.",
    cohort: "the roughly 300 undergraduates I have taught over eight years",
    basis: "the quality of independent research work",
    growth: "",
    limit: "public speaking, where she is building confidence",
    signName: "Elena Park",
    signTitle: "Associate Professor of Chemistry",
    signInstitution: "Northfield University",
    signEmail: "e.park@example.edu",
  },
  ...RECOMMENDATION_BANK,
} as const;

interface Form extends Omit<RecommendationInput, "date"> {
  dateIso: string;
}

const EMPTY: Form = {
  dateIso: "",
  recipient: "",
  name: "",
  shortName: "",
  purpose: "",
  capacity: "",
  duration: "",
  context: "",
  evidence: EMPTY_EVIDENCE,
  rank: "none",
  cohort: "",
  basis: "",
  ownComparison: "",
  growth: "",
  limit: "",
  endorsement: "strong",
  signName: "",
  signTitle: "",
  signInstitution: "",
  signEmail: "",
  signOff: "sincerely",
};

type TextKey =
  | "recipient"
  | "name"
  | "shortName"
  | "purpose"
  | "capacity"
  | "duration"
  | "context"
  | "cohort"
  | "basis"
  | "ownComparison"
  | "growth"
  | "limit"
  | "signName"
  | "signTitle"
  | "signInstitution"
  | "signEmail";

const SLOTS = Array.from({ length: EVIDENCE_SLOTS }, (_, index) => index + 1);

export default function RecommendationLetterGeneratorWidget() {
  const strings = useToolUi(RECOMMENDATION_LETTER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>(EMPTY);

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const text = (key: TextKey, id: string) => ({
    id: `rlg-${id}`,
    onChange: (value: string) => set(key, value),
    value: form[key],
  });
  const setEvidence = (
    slot: number,
    key: "quality" | "observation",
    value: string
  ) =>
    setForm((current) => ({
      ...current,
      evidence: current.evidence.map((item, index) =>
        index === slot ? { ...item, [key]: value } : item
      ),
    }));

  const input: RecommendationInput = useMemo(
    () => ({ ...form, date: formatLetterDate(form.dateIso, locale) }),
    [form, locale]
  );
  const paragraphs = useMemo(
    () => buildRecommendationLetter(input, strings),
    [input, strings]
  );
  const notes = useMemo(
    () => reviewLetter(input, paragraphs),
    [input, paragraphs]
  );
  const blanks = countPlaceholders(paragraphs);
  const status =
    blanks > 0
      ? tPlural(strings.placeholdersLeft, locale, blanks, {
          count: fmt(blanks),
        })
      : strings.noPlaceholders;
  const words = (count: number) =>
    tPlural(strings.wordCount, locale, count, { count: fmt(count) });

  function noteText(note: ReviewNote): string {
    switch (note.id) {
      case "evidence-thin":
        return t(strings.review[note.id], {
          slot: fmt(note.slot),
          words: words(note.words),
        });
      case "letter-short":
      case "letter-long":
        return t(strings.review[note.id], { words: words(note.words) });
      default:
        return strings.review[note.id];
    }
  }

  function fillExample() {
    const {
      quality1,
      quality2,
      quality3,
      observation1,
      observation2,
      observation3,
      ...rest
    } = strings.example;
    setForm({
      ...EMPTY,
      ...rest,
      rank: "top5",
      endorsement: "strong",
      evidence: [
        { quality: quality1, observation: observation1 },
        { quality: quality2, observation: observation2 },
        { quality: quality3, observation: observation3 },
      ],
    });
  }

  return (
    <>
      <section className="space-y-5">
        <FormHeader
          clearLabel={strings.clear}
          exampleLabel={strings.tryExample}
          heading={strings.formHeading}
          onClear={() => setForm(EMPTY)}
          onExample={fillExample}
        />
        <details className={`${NOTICE} space-y-2`}>
          <summary className="cursor-pointer font-medium text-foreground">
            {strings.ferpaSummary}
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {strings.ferpaPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </details>

        <FieldGroup title={strings.studentSection}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              {...text("name", "name")}
              label={strings.nameLabel}
              placeholder={strings.namePlaceholder}
            />
            <TextField
              {...text("shortName", "short")}
              hint={strings.shortNameHint}
              label={strings.shortNameLabel}
              placeholder={strings.shortNamePlaceholder}
            />
            <TextField
              {...text("purpose", "purpose")}
              label={strings.purposeLabel}
              placeholder={strings.purposePlaceholder}
            />
            <TextField
              {...text("recipient", "recipient")}
              hint={strings.recipientHint}
              label={strings.recipientLabel}
              placeholder={strings.recipientPlaceholder}
            />
            <TextField
              id="rlg-date"
              label={strings.dateLabel}
              onChange={(value) => set("dateIso", value)}
              type="date"
              value={form.dateIso}
            />
          </div>
        </FieldGroup>

        <FieldGroup title={strings.relationshipSection}>
          <TextField
            {...text("capacity", "capacity")}
            hint={strings.capacityHint}
            label={strings.capacityLabel}
            placeholder={strings.capacityPlaceholder}
          />
          <TextField
            {...text("duration", "duration")}
            label={strings.durationLabel}
            placeholder={strings.durationPlaceholder}
          />
          <CompactTextArea
            {...text("context", "context")}
            label={strings.contextLabel}
            placeholder={strings.contextPlaceholder}
            rows={2}
          />
        </FieldGroup>

        <FieldGroup title={strings.evidenceSection}>
          <p className={HINT}>{strings.evidenceHint}</p>
          {SLOTS.map((n) => (
            <div className="space-y-3" key={n}>
              <TextField
                id={`rlg-quality-${n}`}
                label={t(strings.qualityLabel, { n })}
                onChange={(value) => setEvidence(n - 1, "quality", value)}
                placeholder={strings.qualityPlaceholder}
                value={form.evidence[n - 1].quality}
              />
              <CompactTextArea
                id={`rlg-observation-${n}`}
                label={t(strings.observationLabel, { n })}
                onChange={(value) => setEvidence(n - 1, "observation", value)}
                placeholder={strings.observationPlaceholder}
                rows={4}
                value={form.evidence[n - 1].observation}
              />
            </div>
          ))}
        </FieldGroup>

        <FieldGroup title={strings.comparisonSection}>
          <p className={HINT}>{strings.comparisonHint}</p>
          <SelectField
            id="rlg-rank"
            label={strings.rankLabel}
            onChange={(value: Rank) => set("rank", value)}
            options={RANKS.map((key) => ({
              value: key,
              label: strings.rankOptions[key],
            }))}
            value={form.rank}
          />
          {form.rank === "own" ? (
            <CompactTextArea
              {...text("ownComparison", "own")}
              label={strings.ownLabel}
              placeholder={strings.ownPlaceholder}
              rows={2}
            />
          ) : null}
          {form.rank !== "none" && form.rank !== "own" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                {...text("cohort", "cohort")}
                label={strings.cohortLabel}
                placeholder={strings.cohortPlaceholder}
              />
              <TextField
                {...text("basis", "basis")}
                label={strings.basisLabel}
                placeholder={strings.basisPlaceholder}
              />
            </div>
          ) : null}
          <CompactTextArea
            {...text("growth", "growth")}
            label={strings.growthLabel}
            placeholder={strings.growthPlaceholder}
            rows={2}
          />
          <TextField
            {...text("limit", "limit")}
            hint={strings.limitHint}
            label={strings.limitLabel}
            placeholder={strings.limitPlaceholder}
          />
        </FieldGroup>

        <FieldGroup title={strings.endorsementSection}>
          <PillGroup
            label={strings.endorsementLabel}
            name="rlg-endorsement"
            onChange={(value: Endorsement) => set("endorsement", value)}
            options={ENDORSEMENTS.map((key) => ({
              value: key,
              label: strings.endorsementOptions[key],
            }))}
            value={form.endorsement}
          />
          <p className={HINT}>{strings.endorsementHint}</p>
        </FieldGroup>

        <FieldGroup title={strings.signSection}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              {...text("signName", "sign-name")}
              label={strings.signNameLabel}
              placeholder={strings.signNamePlaceholder}
            />
            <TextField
              {...text("signTitle", "sign-title")}
              label={strings.signTitleLabel}
              placeholder={strings.signTitlePlaceholder}
            />
            <TextField
              {...text("signInstitution", "sign-institution")}
              label={strings.signInstitutionLabel}
              placeholder={strings.signInstitutionPlaceholder}
            />
            <TextField
              {...text("signEmail", "sign-email")}
              label={strings.signEmailLabel}
              placeholder={strings.signEmailPlaceholder}
            />
            <SelectField
              id="rlg-signoff"
              label={strings.signOffLabel}
              onChange={(value: RecSignOff) => set("signOff", value)}
              options={REC_SIGN_OFFS.map((key) => ({
                value: key,
                label: strings.signOffOptions[key],
              }))}
              value={form.signOff}
            />
          </div>
        </FieldGroup>
      </section>

      <DraftPanel
        filename={strings.downloadFilename}
        heading={strings.draftHeading}
        paragraphs={paragraphs}
        status={status}
        strings={strings}
      >
        <section aria-live="polite" className="space-y-2">
          <h3 className="font-semibold text-foreground text-lg">
            {strings.reviewHeading}
          </h3>
          {notes.length === 0 ? (
            <p className={HINT}>{strings.reviewNone}</p>
          ) : (
            <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
              {notes.map((note) => (
                <li key={`${note.id}-${"slot" in note ? note.slot : 0}`}>
                  {noteText(note)}
                </li>
              ))}
            </ul>
          )}
        </section>
      </DraftPanel>
    </>
  );
}
